import React from 'react'

import {
  AlignItems,
  Body1,
  Box,
  Button,
  Display,
  FlexDirection,
  Heading1,
  Join,
  JustifyContent,
  LoadingSpinner as Loading,
  Space,
} from '@gousto-internal/citrus-react'
import type Immutable from 'immutable'
import { Helmet } from 'react-helmet'

import { isShareableOffer } from 'selectors/rafOffer'

import { BoostedRafSharing } from './BoostedRafSharing/BoostedRafSharing'
import { RafSharing } from './RafSharing/RafSharing'

import css from './Referral.css'

export type IRAFOffer = Immutable.Map<
  string,
  | string
  | string[]
  | { value: number; unit: string; formatted: string }
  | Immutable.Map<string, string>
  | Immutable.Map<string, IRAFOffer>
>

const PAGE_TITLE = 'Gousto Referrals | Invite Your Friends To Try Gousto'

const StatusContainer: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Box
    minHeight="350px"
    paddingV={4}
    display={Display.Flex}
    flexDirection={FlexDirection.Column}
    alignItems={AlignItems.Center}
    justifyContent={JustifyContent.Center}
  >
    {children}
  </Box>
)

interface Props {
  device: string
  displayLink: string
  expiry?: string | null
  hasError: boolean
  isLoading: boolean
  offerTitle: string
  onRetry: () => void
  rafOffer: IRAFOffer
  trackingReferFriend: () => void
  trackingReferFriendSocialSharing: () => void
  trackUserFreeFoodLinkShare: () => void
  userFirstName: string
}

export const ReferralPresentation: React.FC<Props> = ({
  device,
  displayLink,
  expiry,
  hasError,
  isLoading,
  offerTitle,
  onRetry,
  rafOffer,
  trackingReferFriend,
  trackingReferFriendSocialSharing,
  trackUserFreeFoodLinkShare,
  userFirstName,
}: Props) => {
  const renderContent = () => {
    if (isLoading) {
      return (
        <StatusContainer>
          <Loading />
        </StatusContainer>
      )
    }

    if (hasError || !isShareableOffer(rafOffer)) {
      return (
        <StatusContainer>
          <Join with={<Space size={3} />}>
            <Heading1>Something went wrong</Heading1>
            <Body1>Please try again later</Body1>
            <Button onClick={onRetry}>Refresh</Button>
          </Join>
        </StatusContainer>
      )
    }

    // boost view takes priority over non-boosted views - show this view when boost ui is enabled and expiry date
    if (expiry) {
      return (
        <div id="referral-code-box" className={css.containerBackgroundRafBoostRefreshUI}>
          <BoostedRafSharing
            rafOffer={rafOffer}
            expiry={new Date(expiry)}
            userRAFLink={displayLink}
            userFirstName={userFirstName}
            device={device}
            trackingReferFriendSocialSharing={trackingReferFriendSocialSharing}
            trackUserFreeFoodLinkShare={trackUserFreeFoodLinkShare}
            trackingReferFriend={trackingReferFriend}
          />
        </div>
      )
    }

    return (
      <div id="referral-code-box">
        <RafSharing
          device={device}
          userRAFLink={displayLink}
          userFirstName={userFirstName}
          offer={rafOffer}
          offerTitle={offerTitle}
          trackingReferFriendSocialSharing={trackingReferFriendSocialSharing}
          trackUserFreeFoodLinkShare={trackUserFreeFoodLinkShare}
          trackingReferFriend={trackingReferFriend}
        />
      </div>
    )
  }

  return (
    <div>
      <Helmet title={PAGE_TITLE} />
      {renderContent()}
    </div>
  )
}
